/**
 * The data horizon (2026-10-05, the user's reference image): a panoramic band of glass that
 * arches above the dashboard arc, carrying the room's background layer of information.
 *
 * - The study overview that faced the table moved onto the centre screen (2026-10-05); its
 *   globe and enrolment curve are drawn by the helpers exported here.
 * - Two zone modules frame the dashboard groups beneath them: Study Health on the left,
 *   Patient Safety on the right.
 * - Between and around them the band is frosted glass with slow streams of light.
 *
 * Everything shown comes from the study file and the live room summary; nothing is invented.
 * Two draw calls: the band, and the modules (one atlas, redrawn only when the data changes).
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { StudyFile } from '@clinspace/shared';
import { BODY_FONT, DISPLAY_FONT } from '../ui/theme.ts';
import type { ChamberTime } from './chamber.ts';

export interface HorizonData {
  enrolled: number;
  target: number;
  openQueries: number;
  lastDataCut: string;
  siteCount?: number;
  study?: Pick<StudyFile, 'study' | 'dashboards' | 'sites' | 'subjects'>;
}

export interface Horizon {
  group: THREE.Group;
  set(data: HorizonData): void;
  dispose(): void;
}

const ATLAS_W = 1024;
/** Atlas rows: the two zone modules, then the words. */
const ROWS = {
  left: { y: 0, h: 190 },
  right: { y: 190, h: 190 },
  /** Two word slots side by side (each half the atlas width). */
  words: { y: 380, h: 150 },
};
const ATLAS_H = 530;
/** The words on the band between the modules (the user's reference image). */
const WORDS = [
  ['BETTER', 'SCIENCE', 'BRIGHTER LIVES'],
  ['DATA', 'INTO', 'DISCOVERIES'],
];
const DEG = Math.PI / 180;

export interface HorizonLayout {
  centre: THREE.Vector3;
  radius: number;
  /** Angles (radians, the arc's convention: x = cos a, z = sin a) and heights per module. */
  left: { from: number; to: number; y0: number; y1: number };
  right: { from: number; to: number; y0: number; y1: number };
  /** The two gaps in the band that carry words. */
  words: [
    { from: number; to: number; y0: number; y1: number },
    { from: number; to: number; y0: number; y1: number },
  ];
  /** The glass band; `gap` leaves out the stretch behind the centre screen's flat wall. */
  band: { from: number; to: number; y0: number; y1: number; gap?: { from: number; to: number } };
}

function arcStrip(
  centre: THREE.Vector3,
  radius: number,
  from: number,
  to: number,
  y0: number,
  y1: number,
  v0: number,
  v1: number,
  steps = 48,
): THREE.BufferGeometry {
  const pos: number[] = [];
  const uv: number[] = [];
  const index: number[] = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const a = from + (to - from) * t;
    const x = centre.x + Math.cos(a) * radius;
    const z = centre.z + Math.sin(a) * radius;
    pos.push(x, y0, z, x, y1, z);
    uv.push(t, v0, t, v1);
    if (i > 0) {
      const k = (i - 1) * 2;
      index.push(k, k + 1, k + 2, k + 1, k + 3, k + 2);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(index);
  return g;
}

export function buildHorizon(time: ChamberTime, layout: HorizonLayout): Horizon {
  const group = new THREE.Group();
  group.name = 'data-horizon';
  const { centre, radius } = layout;

  // The frosted glass band with slow streams of light.
  const b = layout.band;
  const strip = (from: number, to: number) =>
    arcStrip(centre, radius + 0.08, from, to, b.y0, b.y1, 0, 1, 80);
  const band = new THREE.Mesh(
    b.gap
      ? mergeGeometries([strip(b.from, b.gap.from), strip(b.gap.to, b.to)])!
      : strip(b.from, b.to),
    new THREE.ShaderMaterial({
      uniforms: { uTime: time },
      vertexShader: /* glsl */ `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }`,
      fragmentShader: /* glsl */ `
        uniform float uTime;
        varying vec2 vUv;
        void main() {
          float y = vUv.y;
          // Frosted glass: a cool tint, denser toward the middle.
          float a = 0.18 + 0.1 * (1.0 - abs(y - 0.5) * 2.0);
          vec3 c = vec3(0.12, 0.30, 0.40);
          // Streams of light flowing along the band in a few lanes.
          float lanes = 0.0;
          for (int i = 0; i < 4; i++) {
            float fi = float(i);
            float lane = 0.22 + fi * 0.18;
            float w = max(0.0, 1.0 - abs(y - lane) * 160.0);
            float x = fract(vUv.x * (2.0 + fi) - uTime * (0.01 + fi * 0.004) * (mod(fi, 2.0) < 0.5 ? 1.0 : -1.0));
            lanes += w * (0.25 + 0.75 * pow(1.0 - x, 12.0));
          }
          c += vec3(0.55, 0.88, 0.95) * lanes * 0.8;
          a += lanes * 0.5;
          // Warm light along the top and foot of the band.
          float edge = max(0.0, 1.0 - (1.0 - y) * 70.0) + max(0.0, 1.0 - y * 70.0);
          c += vec3(1.0, 0.86, 0.66) * edge;
          a += edge * 0.8;
          gl_FragColor = vec4(c, clamp(a, 0.0, 1.0));
        }`,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      forceSinglePass: true,
      toneMapped: false,
    }),
  );
  band.name = 'horizon-band';
  band.renderOrder = 4;
  group.add(band);

  // The modules: three arc strips mapped onto one atlas.
  const canvas = document.createElement('canvas');
  canvas.width = ATLAS_W;
  canvas.height = ATLAS_H;
  const ctx = canvas.getContext('2d')!;
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 8;
  const v = (row: { y: number; h: number }): [number, number] => [
    1 - (row.y + row.h) / ATLAS_H,
    1 - row.y / ATLAS_H,
  ];
  const strips: THREE.BufferGeometry[] = (
    [
      [layout.left, ROWS.left],
      [layout.right, ROWS.right],
    ] as const
  ).map(([m, row]) => {
    const [v0, v1] = v(row);
    return arcStrip(centre, radius, m.from, m.to, m.y0, m.y1, v0, v1, 64);
  });
  // Word slots: each maps one half of the words row.
  layout.words.forEach((m, i) => {
    const [v0, v1] = v(ROWS.words);
    const g = arcStrip(centre, radius, m.from, m.to, m.y0, m.y1, v0, v1, 32);
    const uv = g.attributes.uv!;
    for (let k = 0; k < uv.count; k++) uv.setX(k, (i + uv.getX(k)) / 2);
    strips.push(g);
  });
  const merged = mergeStrips(strips);
  const modules = new THREE.Mesh(
    merged,
    new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      toneMapped: false,
    }),
  );
  modules.name = 'horizon-modules';
  modules.renderOrder = 5;
  group.add(modules);

  let lastKey = '';
  return {
    group,
    set: (data) => {
      const key = JSON.stringify([
        data.enrolled,
        data.target,
        data.openQueries,
        data.lastDataCut,
        data.siteCount,
        data.study?.study.code ?? null,
      ]);
      if (key === lastKey) return;
      lastKey = key;
      ctx.clearRect(0, 0, ATLAS_W, ATLAS_H);
      drawZone(ctx, ROWS.left, 'STUDY HEALTH', 'Enrolment and site performance', healthStats(data));
      drawZone(
        ctx,
        ROWS.right,
        'PATIENT SAFETY',
        'Safety signals and risk monitoring',
        safetyStats(data),
      );
      WORDS.forEach((lines, i) => drawWords(ctx, i * (ATLAS_W / 2), ROWS.words, lines));
      texture.needsUpdate = true;
    },
    dispose: () => {
      texture.dispose();
      for (const m of [band, modules]) {
        m.geometry.dispose();
        (m.material as THREE.Material).dispose();
      }
    },
  };
}

function mergeStrips(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const pos: number[] = [];
  const uv: number[] = [];
  const index: number[] = [];
  for (const p of parts) {
    const base = pos.length / 3;
    pos.push(...(p.attributes.position!.array as Float32Array));
    uv.push(...(p.attributes.uv!.array as Float32Array));
    for (const i of p.index!.array) index.push(base + i);
    p.dispose();
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(index);
  return g;
}

/* ---------------- Drawing ---------------- */

const TEAL = '#3fd6c8';
const IVORY = '#eef6fa';
const MUTED = '#9db7c6';

function glass(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number): void {
  const g = ctx.createLinearGradient(0, y, 0, y + h);
  g.addColorStop(0, 'rgba(20,56,78,0.86)');
  g.addColorStop(1, 'rgba(8,28,42,0.9)');
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.roundRect(x + 2, y + 2, w - 4, h - 4, 14);
  ctx.fill();
  ctx.save();
  ctx.shadowColor = 'rgba(150,230,245,0.7)';
  ctx.shadowBlur = 6;
  ctx.strokeStyle = 'rgba(170,236,248,0.6)';
  ctx.lineWidth = 1.5;
  ctx.stroke();
  ctx.restore();
}

function label(
  ctx: CanvasRenderingContext2D,
  s: string,
  x: number,
  y: number,
  size: number,
  colour: string,
  weight = 500,
  display = false,
  spacing = 0,
  maxWidth?: number,
): void {
  ctx.font = `${weight} ${size}px ${display ? DISPLAY_FONT : BODY_FONT}`;
  ctx.letterSpacing = `${spacing}px`;
  ctx.fillStyle = colour;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.fillText(s, x, y, maxWidth);
  ctx.letterSpacing = '0px';
}

/** Weekly cumulative enrolment: the plan dashed, the actual as a glowing filled curve. */
export function drawCumulative(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  points: { actual: number; planned: number }[],
): void {
  if (points.length < 2) return;
  const max = Math.max(1, ...points.map((p) => Math.max(p.actual, p.planned)));
  const px = (i: number) => x + (i / (points.length - 1)) * w;
  const py = (v: number) => y + h - (v / max) * (h - 10);
  // Planned: a faint dashed line; actual: a filled glowing curve.
  ctx.setLineDash([4, 4]);
  ctx.strokeStyle = 'rgba(190,235,250,0.4)';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  points.forEach((p, i) =>
    i ? ctx.lineTo(px(i), py(p.planned)) : ctx.moveTo(px(i), py(p.planned)),
  );
  ctx.stroke();
  ctx.setLineDash([]);
  const fill = ctx.createLinearGradient(0, y, 0, y + h);
  fill.addColorStop(0, 'rgba(63,214,200,0.35)');
  fill.addColorStop(1, 'rgba(63,214,200,0)');
  ctx.beginPath();
  points.forEach((p, i) => (i ? ctx.lineTo(px(i), py(p.actual)) : ctx.moveTo(px(i), py(p.actual))));
  ctx.lineTo(px(points.length - 1), y + h);
  ctx.lineTo(x, y + h);
  ctx.closePath();
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.save();
  ctx.shadowColor = 'rgba(63,214,200,0.9)';
  ctx.shadowBlur = 6;
  ctx.strokeStyle = TEAL;
  ctx.lineWidth = 2.2;
  ctx.beginPath();
  points.forEach((p, i) => (i ? ctx.lineTo(px(i), py(p.actual)) : ctx.moveTo(px(i), py(p.actual))));
  ctx.stroke();
  ctx.restore();
}

/** An orthographic globe turned to the study's sites, with arcs linking them. */
export function drawGlobe(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  siteList: { mapX: number; mapY: number }[],
): void {
  const sites = siteList.map((s) => ({
    lon: s.mapX * 360 - 180,
    lat: 90 - s.mapY * 180,
  }));
  const lon0 = sites.length ? sites.reduce((a, s) => a + s.lon, 0) / sites.length : -40;
  const lat0 = 25;
  const project = (lon: number, lat: number): [number, number, boolean] => {
    const l = ((lon - lon0) * Math.PI) / 180;
    const p = (lat * Math.PI) / 180;
    const p0 = (lat0 * Math.PI) / 180;
    const cosc = Math.sin(p0) * Math.sin(p) + Math.cos(p0) * Math.cos(p) * Math.cos(l);
    const x = r * Math.cos(p) * Math.sin(l);
    const y = r * (Math.cos(p0) * Math.sin(p) - Math.sin(p0) * Math.cos(p) * Math.cos(l));
    return [cx + x, cy - y, cosc > 0];
  };
  // Atmosphere and sphere.
  const halo = ctx.createRadialGradient(cx, cy, r * 0.8, cx, cy, r * 1.25);
  halo.addColorStop(0, 'rgba(90,190,230,0.35)');
  halo.addColorStop(1, 'rgba(90,190,230,0)');
  ctx.fillStyle = halo;
  ctx.beginPath();
  ctx.arc(cx, cy, r * 1.25, 0, Math.PI * 2);
  ctx.fill();
  const body = ctx.createRadialGradient(cx - r * 0.3, cy - r * 0.35, r * 0.1, cx, cy, r);
  body.addColorStop(0, '#1d5a7a');
  body.addColorStop(1, '#071a28');
  ctx.fillStyle = body;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();
  // Graticule.
  ctx.strokeStyle = 'rgba(150,220,240,0.22)';
  ctx.lineWidth = 1;
  for (let lat = -60; lat <= 60; lat += 30) {
    ctx.beginPath();
    let pen = false;
    for (let lon = -180; lon <= 180; lon += 5) {
      const [x, y, front] = project(lon, lat);
      if (front && pen) ctx.lineTo(x, y);
      else if (front) ctx.moveTo(x, y);
      pen = front;
    }
    ctx.stroke();
  }
  for (let lon = -180; lon < 180; lon += 30) {
    ctx.beginPath();
    let pen = false;
    for (let lat = -90; lat <= 90; lat += 5) {
      const [x, y, front] = project(lon, lat);
      if (front && pen) ctx.lineTo(x, y);
      else if (front) ctx.moveTo(x, y);
      pen = front;
    }
    ctx.stroke();
  }
  // Arcs between consecutive sites, then the sites.
  ctx.strokeStyle = 'rgba(255,214,150,0.75)';
  ctx.lineWidth = 1.4;
  for (let i = 1; i < sites.length; i++) {
    const [ax, ay, af] = project(sites[i - 1]!.lon, sites[i - 1]!.lat);
    const [bx, by, bf] = project(sites[i]!.lon, sites[i]!.lat);
    if (!af || !bf) continue;
    const mx = (ax + bx) / 2;
    const my = (ay + by) / 2 - Math.hypot(bx - ax, by - ay) * 0.35;
    ctx.beginPath();
    ctx.moveTo(ax, ay);
    ctx.quadraticCurveTo(mx, my, bx, by);
    ctx.stroke();
  }
  for (const s of sites) {
    const [x, y, front] = project(s.lon, s.lat);
    if (!front) continue;
    const g = ctx.createRadialGradient(x, y, 0, x, y, 9);
    g.addColorStop(0, 'rgba(220,250,255,1)');
    g.addColorStop(0.4, 'rgba(120,230,245,0.7)');
    g.addColorStop(1, 'rgba(120,230,245,0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(x, y, 9, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.strokeStyle = 'rgba(170,236,248,0.5)';
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.stroke();
}

interface Stat {
  value: string;
  name: string;
  ring?: number;
  colour?: string;
}

function healthStats(data: HorizonData): Stat[] {
  const d = data.study?.dashboards;
  const pct = data.target ? Math.round((data.enrolled / data.target) * 100) : 0;
  return [
    { value: `${data.enrolled}/${data.target}`, name: 'Enrolled vs target', ring: pct / 100 },
    { value: d ? `${Math.round(d.visitCompliance.kpis.onTimePct)}%` : '–', name: 'Visits on time' },
    {
      value: d ? `${Math.round(d.enrollment.kpis.screenFailureRate)}%` : '–',
      name: 'Screen failure',
    },
    { value: d ? `${Math.round(d.dataCleaning.kpis.cleanPct)}%` : '–', name: 'Clean forms' },
  ];
}

function safetyStats(data: HorizonData): Stat[] {
  const d = data.study?.dashboards;
  return [
    {
      value: d ? String(d.saeReview.kpis.total) : '–',
      name: 'Serious AEs',
      colour: d && d.saeReview.kpis.total ? '#ffb86b' : undefined,
    },
    { value: d ? String(d.saeReview.kpis.unexpected) : '–', name: 'Unexpected' },
    {
      value: d ? String(d.labAbnormality.kpis.subjectsWithGrade3Plus) : '–',
      name: 'Grade 3+ labs',
    },
    {
      value: d ? String(d.rbm.kpis.sitesRed) : '–',
      name: 'Sites at high risk',
      colour: d && d.rbm.kpis.sitesRed ? '#ff8a9a' : undefined,
    },
  ];
}

function drawZone(
  ctx: CanvasRenderingContext2D,
  row: { y: number; h: number },
  title: string,
  subtitle: string,
  stats: Stat[],
): void {
  const { y: y0, h } = row;
  glass(ctx, 0, y0, ATLAS_W, h);
  label(ctx, title, 26, y0 + 56, 34, IVORY, 600, true, 2);
  label(ctx, subtitle, 26, y0 + 88, 18, MUTED);
  const x0 = 380;
  const w = (ATLAS_W - x0 - 20) / stats.length;
  stats.forEach((s, i) => {
    const x = x0 + i * w;
    if (s.ring !== undefined) {
      ctx.strokeStyle = 'rgba(190,235,250,0.15)';
      ctx.lineWidth = 7;
      ctx.beginPath();
      ctx.arc(x + 40, y0 + h / 2, 32, 0, Math.PI * 2);
      ctx.stroke();
      ctx.save();
      ctx.shadowColor = 'rgba(63,214,200,0.9)';
      ctx.shadowBlur = 6;
      ctx.strokeStyle = TEAL;
      ctx.beginPath();
      ctx.arc(
        x + 40,
        y0 + h / 2,
        32,
        -Math.PI / 2,
        -Math.PI / 2 + Math.PI * 2 * Math.min(1, s.ring),
      );
      ctx.stroke();
      ctx.restore();
      label(ctx, `${Math.round(s.ring * 100)}%`, x + 22, y0 + h / 2 + 7, 18, IVORY, 600, true);
      label(ctx, s.value, x + 84, y0 + h / 2 - 2, 22, IVORY, 600, true, 0, w - 90);
      label(ctx, s.name, x + 84, y0 + h / 2 + 20, 13, MUTED, 500, false, 0, w - 90);
      return;
    }
    label(ctx, s.value, x + 10, y0 + h / 2 + 4, 40, s.colour ?? IVORY, 600, true, 0, w - 16);
    label(ctx, s.name, x + 10, y0 + h / 2 + 30, 14, MUTED, 500, false, 0, w - 16);
  });
  // A thin ribbon of light under the module title.
  const g = ctx.createLinearGradient(26, 0, 340, 0);
  g.addColorStop(0, 'rgba(143,227,240,0.9)');
  g.addColorStop(1, 'rgba(143,227,240,0)');
  ctx.fillStyle = g;
  ctx.fillRect(26, y0 + 104, 314, 2);
}

/** Words in light, centred in their slot, letterspaced, one line each. */
function drawWords(
  ctx: CanvasRenderingContext2D,
  x0: number,
  row: { y: number; h: number },
  lines: string[],
): void {
  const w = ATLAS_W / 2;
  const lh = 40;
  const top = row.y + (row.h - lh * lines.length) / 2 + 30;
  ctx.save();
  ctx.shadowColor = 'rgba(255,230,190,0.7)';
  ctx.shadowBlur = 8;
  lines.forEach((l, i) => {
    ctx.font = `500 28px ${DISPLAY_FONT}`;
    ctx.letterSpacing = '9px';
    ctx.fillStyle = 'rgba(255,240,220,0.92)';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    ctx.fillText(l, x0 + w / 2, top + i * lh, w - 20);
  });
  ctx.letterSpacing = '0px';
  ctx.restore();
}

export { DEG as HORIZON_DEG };
